export interface BookingRates {
  perHour?: number;
  perSession?: number;
  perGig?: number;
  perTake?: number;
  currency: string;         // ISO 4217, default 'SEK'
  type: 'fixed' | 'pwyw' | 'negotiable';
  minimumRate?: number;
}

export interface BookingProfile {
  userId: string;
  displayName: string;
  profileImageUrl?: string;
  bookingRoles: string[];   // roles they're bookable for
  bookingBio?: string;
  bookingCity?: string;
  bookingAvailability?: string;
  rates?: BookingRates;
  availableForBooking: boolean;
  genres?: string[];
  /** What a musician plays (users.instruments). */
  instruments?: string[];
  followerCount?: number;
  verified?: boolean;
  portfolioUrl?: string;
  /** Aggregated client ratings from paid/accepted bookings */
  bookingRatingAvg?: number;   // 1–5, rounded to 1 decimal
  bookingRatingCount?: number;
}

export interface BookingRequest {
  id?: string;
  clientId: string;
  clientName: string;
  clientEmail: string;
  clientPhone?: string;
  /** Ticked "become a BB customer and get offers" when booking (never pre-ticked). */
  bbCustomerOptIn?: boolean;
  talentId: string;
  talentName: string;
  proposedDate?: string;     // ISO date string
  proposedFee?: number;
  currency?: string;
  eventType?: string;
  location?: string;
  message: string;
  status: 'pending' | 'reviewed' | 'accepted' | 'declined' | 'paid';
  createdAt?: any;           // Firestore Timestamp
  // ── Payroll (set by the booking-payment webhook) ──
  paidAt?: any;              // Firestore Timestamp
  stripeSessionId?: string;
  stripePaymentIntentId?: string;
  // ── Client review (set via /api/bookings/review from the pay-link page) ──
  rating?: number;           // 1–5 stars
  review?: string;           // client comment
  reviewedAt?: any;          // Firestore Timestamp
}

export interface SupporterEntry {
  id?: string;
  userId?: string;           // undefined = anonymous
  displayName: string;
  totalAmount: number;
  currency: string;
  message?: string;
  anonymous: boolean;
  lastContribution?: any;    // Firestore Timestamp
  avatarUrl?: string;
}

export interface ParticipationClaim {
  id?: string;
  userId: string;
  displayName: string;
  imageUrl?: string;
  role: string;
  note?: string;
  status: 'pending' | 'approved' | 'rejected';
  claimedAt?: any;
}

export const TALENT_ROLES = [
  { id: 'all',            label: 'All Talent',      icon: 'star',          firestoreRole: null },
  { id: 'artist',         label: 'Artists',         icon: 'mic',           firestoreRole: 'Artist' },
  { id: 'musician',       label: 'Musicians',       icon: 'music',         firestoreRole: 'Musician' },
  { id: 'dj',             label: 'DJs',             icon: 'headphones',    firestoreRole: 'DJ' },
  { id: 'engineer',         label: 'Engineers',           icon: 'mic2',         firestoreRole: 'Engineer' },
  { id: 'mixing-engineer',  label: 'Mixing Engineers',    icon: 'sliders',      firestoreRole: 'Mixing Engineer' },
  { id: 'mastering-engineer', label: 'Mastering Engineers', icon: 'sliders',    firestoreRole: 'Mastering Engineer' },
  { id: 'producer',         label: 'Producers',           icon: 'sliders',      firestoreRole: 'Producer' },
  { id: 'beatmaker',        label: 'Beatmakers',          icon: 'grid',         firestoreRole: 'Beatmaker' },
  { id: 'songwriter',       label: 'Songwriters',         icon: 'pen',          firestoreRole: 'Songwriter' },
  { id: 'composer',         label: 'Composers',           icon: 'music2',       firestoreRole: 'Composer' },
  { id: 'topliner',         label: 'Topliners',           icon: 'mic',          firestoreRole: 'Topliner' },
  { id: 'vocalist',         label: 'Vocalists',           icon: 'mic',          firestoreRole: 'Vocalist' },
  { id: 'session-musician', label: 'Session Musicians',   icon: 'music',        firestoreRole: 'Session Musician' },
  { id: 'video-director',   label: 'Video Directors',     icon: 'clapperboard', firestoreRole: 'Video Director' },
  { id: 'videographer',     label: 'Videographers',       icon: 'video',        firestoreRole: 'Videographer' },
  { id: 'video-editor',     label: 'Video Editors',       icon: 'film',         firestoreRole: 'Video Editor' },
  { id: 'photographer',     label: 'Photographers',       icon: 'camera',       firestoreRole: 'Photographer' },
  { id: 'graphic',          label: 'Graphic Artists',     icon: 'palette',      firestoreRole: 'Graphic Artist' },
  { id: 'illustrator',      label: 'Illustrators',        icon: 'pencil',       firestoreRole: 'Illustrator' },
  { id: 'designer',         label: 'Designers',           icon: 'layout',       firestoreRole: 'Designer' },
  { id: 'stylist',          label: 'Stylists',            icon: 'shirt',        firestoreRole: 'Stylist' },
  { id: 'makeup-artist',    label: 'Makeup Artists',      icon: 'sparkles',     firestoreRole: 'Makeup Artist' },
  { id: 'dancer',           label: 'Dancers',             icon: 'activity',     firestoreRole: 'Dancer' },
  { id: 'choreographer',    label: 'Choreographers',      icon: 'activity',     firestoreRole: 'Choreographer' },
  { id: 'manager',          label: 'Managers',            icon: 'briefcase',    firestoreRole: 'Manager' },
  { id: 'booking-agent',    label: 'Booking Agents',      icon: 'calendar',     firestoreRole: 'Booking Agent' },
  { id: 'promoter',         label: 'Promoters',           icon: 'megaphone',    firestoreRole: 'Promoter' },
  { id: 'publicist',        label: 'Publicists / PR',     icon: 'megaphone',    firestoreRole: 'Publicist' },
  { id: 'ar',               label: 'A&R',                 icon: 'search',       firestoreRole: 'A&R' },
  { id: 'tour-manager',     label: 'Tour Managers',       icon: 'bus',          firestoreRole: 'Tour Manager' },
] as const;

export type TalentRoleId = typeof TALENT_ROLES[number]['id'];
